import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  CHECKLIST_ITEM_TYPES,
  CILT_LABELS,
  CILT_TYPES,
  FAILURE_BEHAVIOURS,
  SEVERITIES,
  type CiltType,
} from '@odr/shared';

import {
  Badge,
  Button,
  Callout,
  Field,
  Input,
  Modal,
  Select,
  Textarea,
} from '../../components/ui';
import { api } from '../../lib/api';
import { humanise } from '../../lib/format';

export interface ItemDraft {
  id?: string;
  seq: number;
  task: string;
  itemType: string;
  ciltType: string;
  componentId: string | null;
  toolMethod: string | null;
  standard: string | null;
  instruction: string | null;
  isRequired: boolean;
  expectedValue: string | null;
  minValue: number | null;
  maxValue: number | null;
  targetValue: number | null;
  tolerance: number | null;
  recordOnly: boolean;
  unit: string | null;
  failureBehaviour: string;
  requiresPhoto: boolean;
  requiresPhotoOnFail: boolean;
  safetyWarning: string | null;
  qualityWarning: string | null;
  isSafetyCritical: boolean;
  defaultSeverity: string;
  oplId: string | null;
  engineeringStandardId: string | null;
  options: Array<{ value: string; label: string; result: string }>;
}

export const EMPTY_ITEM: ItemDraft = {
  seq: 1,
  task: '',
  itemType: 'PASS_FAIL',
  ciltType: 'OTHER',
  componentId: null,
  toolMethod: null,
  standard: null,
  instruction: null,
  isRequired: true,
  expectedValue: null,
  minValue: null,
  maxValue: null,
  targetValue: null,
  tolerance: null,
  recordOnly: false,
  unit: null,
  failureBehaviour: 'COMMENT',
  requiresPhoto: false,
  requiresPhotoOnFail: true,
  safetyWarning: null,
  qualityWarning: null,
  isSafetyCritical: false,
  defaultSeverity: 'MEDIUM',
  oplId: null,
  engineeringStandardId: null,
  options: [],
};

const ITEM_TYPE_HELP: Record<string, string> = {
  PASS_FAIL: 'Two big buttons: PASS or FAIL. The default for most CILT checks.',
  YES_NO: 'Two big buttons: YES or NO.',
  CONFIRMATION: 'DONE / NOT DONE. Use for a step that must be carried out.',
  NUMERIC: 'A number the operator reads off a gauge or instrument.',
  COUNTER: 'A count — pumps of grease, drops of oil.',
  DROPDOWN: 'Pick one condition from a list you define.',
  MULTI_SELECT: 'Pick any number of conditions from a list you define.',
  TEXT: 'Free text. Carries no automatic judgement.',
  PHOTO: 'A photo is the answer.',
  SIGNATURE: 'The operator types their name to sign.',
};

const FAILURE_HELP: Record<string, string> = {
  NONE: 'Record the answer and move on.',
  COMMENT: 'Make the operator write a short note.',
  EVIDENCE: 'Make them add a note and a photo.',
  PROBLEM_TAG: 'Make them add a note, a photo and raise a Problem Tag.',
  BLOCK: 'As above, and stop the checklist being submitted until it is resolved.',
};

interface Machine {
  id: string;
  code: string;
  name: string;
}

export function ItemFormModal({
  initial,
  machineType,
  onClose,
  onSubmit,
  busy,
  serverError,
}: {
  initial: ItemDraft;
  machineType: string | null;
  onClose: () => void;
  onSubmit: (draft: ItemDraft) => void;
  busy: boolean;
  serverError: string | null;
}) {
  const [draft, setDraft] = useState<ItemDraft>(initial);
  const [machineId, setMachineId] = useState<string>('');

  const set = <K extends keyof ItemDraft>(key: K, value: ItemDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const { data: machines } = useQuery({
    queryKey: ['machines', 'picker'],
    queryFn: () => api.get<{ data: Machine[] }>('/api/machines'),
  });

  const { data: components } = useQuery({
    queryKey: ['components', machineId],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; code: string; name: string }> }>(
        `/api/machines/${machineId}/components`,
      ),
    enabled: Boolean(machineId),
  });

  const { data: opls } = useQuery({
    queryKey: ['opls', 'picker'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; oplNo: string; title: string }> }>(
        '/api/opls?publishedOnly=true&pageSize=200',
      ),
  });

  const { data: standards } = useQuery({
    queryKey: ['standards', 'picker'],
    queryFn: () =>
      api.get<{
        data: Array<{
          id: string;
          code: string;
          parameter: string;
          status: string;
          minValue: number | null;
          maxValue: number | null;
          unit: string | null;
        }>;
      }>('/api/engineering-standards?pageSize=200'),
  });

  const isMeasured = draft.itemType === 'NUMERIC' || draft.itemType === 'COUNTER';
  const isChoice = draft.itemType === 'DROPDOWN' || draft.itemType === 'MULTI_SELECT';

  const linkedStandard = standards?.data.find((s) => s.id === draft.engineeringStandardId);

  /**
   * Mirror the server's publish rules so the author is told immediately, at the
   * field, instead of at publish time three screens away.
   */
  const problems = useMemo(() => {
    const list: string[] = [];
    if (!draft.task.trim()) list.push('Describe what the operator has to do.');

    if (isMeasured) {
      if (!draft.unit?.trim()) {
        list.push('A measured task needs a unit, otherwise the number means nothing.');
      }
      const hasOwnRange =
        draft.minValue != null ||
        draft.maxValue != null ||
        (draft.targetValue != null && draft.tolerance != null);

      if (draft.recordOnly && hasOwnRange) {
        list.push(
          'This is set to "record only" but also has acceptance limits. Those limits would never be applied.',
        );
      }
      if (!draft.recordOnly && !hasOwnRange && !draft.engineeringStandardId) {
        list.push(
          'Give it an acceptance range, link an engineering standard, or tick "record the reading only".',
        );
      }
      if (draft.minValue != null && draft.maxValue != null && draft.minValue > draft.maxValue) {
        list.push('The minimum is greater than the maximum.');
      }
    }

    if (isChoice && draft.options.length < 2) {
      list.push('A choice task needs at least two options.');
    }
    if (isChoice && draft.options.some((o) => !o.label.trim() || !o.value.trim())) {
      list.push('Every option needs a value and a label.');
    }
    return list;
  }, [draft, isMeasured, isChoice]);

  return (
    <Modal
      open
      onClose={onClose}
      title={initial.id ? `Edit task #${initial.seq}` : 'Add a task'}
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            loading={busy}
            disabled={problems.length > 0}
            onClick={() => onSubmit(draft)}
          >
            {initial.id ? 'Save task' : 'Add task'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {serverError ? <Callout tone="alarm">{serverError}</Callout> : null}
        {problems.length > 0 ? (
          <Callout tone="warn" title="Fix before saving">
            <ul className="mt-1 space-y-0.5">
              {problems.map((problem) => (
                <li key={problem}>• {problem}</li>
              ))}
            </ul>
          </Callout>
        ) : null}

        <Field label="What does the operator do?" required>
          <Textarea
            value={draft.task}
            onChange={(event) => set('task', event.target.value)}
            rows={2}
            placeholder="e.g. Deep clean sealing jaws (top & bottom)"
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Answer type" hint={ITEM_TYPE_HELP[draft.itemType]}>
            <Select
              value={draft.itemType}
              onChange={(event) => {
                const next = event.target.value;
                set('itemType', next);
                // Seed a sensible pair of options so a choice task is usable
                // straight away rather than immediately invalid.
                if (
                  (next === 'DROPDOWN' || next === 'MULTI_SELECT') &&
                  draft.options.length < 2
                ) {
                  set('options', [
                    { value: 'GOOD', label: 'Good', result: 'NORMAL' },
                    { value: 'ABNORMAL', label: 'Abnormal', result: 'ABNORMAL' },
                  ]);
                }
              }}
            >
              {CHECKLIST_ITEM_TYPES.map((type) => (
                <option key={type} value={type}>
                  {humanise(type)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="CILT activity">
            <Select value={draft.ciltType} onChange={(event) => set('ciltType', event.target.value)}>
              {CILT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {CILT_LABELS[type as CiltType]}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Tool or method" hint="How they check it.">
            <Input
              value={draft.toolMethod ?? ''}
              onChange={(event) => set('toolMethod', event.target.value || null)}
              placeholder="e.g. Brass brush + cleaner"
            />
          </Field>

          <Field label="Standard" hint="What good looks like.">
            <Input
              value={draft.standard ?? ''}
              onChange={(event) => set('standard', event.target.value || null)}
              placeholder="e.g. No residue visible"
            />
          </Field>
        </div>

        <Field label="Extra instruction" hint="Optional. Shown under the task on the phone.">
          <Textarea
            value={draft.instruction ?? ''}
            onChange={(event) => set('instruction', event.target.value || null)}
            rows={2}
          />
        </Field>

        {/* --- Machine component --- */}
        <fieldset className="rounded-lg border border-steel-200 p-3">
          <legend className="px-1 text-sm font-bold text-steel-800">Component</legend>
          <p className="mb-2 text-xs text-steel-500">
            Optional, but it is what groups repeat failures by component. When this checklist
            runs on another machine, ODR matches the component by code.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <Select
              value={machineId}
              onChange={(event) => setMachineId(event.target.value)}
              aria-label="Machine"
            >
              <option value="">Choose a machine…</option>
              {(machines?.data ?? []).map((machine) => (
                <option key={machine.id} value={machine.id}>
                  {machine.code} — {machine.name}
                </option>
              ))}
            </Select>
            <Select
              value={draft.componentId ?? ''}
              onChange={(event) => set('componentId', event.target.value || null)}
              disabled={!machineId}
              aria-label="Component"
            >
              <option value="">No component</option>
              {(components?.data ?? []).map((component) => (
                <option key={component.id} value={component.id}>
                  {component.name}
                </option>
              ))}
            </Select>
          </div>
          {machineType ? (
            <p className="mt-2 text-xs text-steel-400">
              This checklist is intended for: {machineType}
            </p>
          ) : null}
        </fieldset>

        {/* --- Measurement --- */}
        {isMeasured ? (
          <fieldset className="rounded-lg border border-info-500/40 bg-info-50/40 p-3">
            <legend className="px-1 text-sm font-bold text-steel-800">Measurement</legend>

            <div className="grid gap-2 sm:grid-cols-2">
              <Field label="Unit" required>
                <Input
                  value={draft.unit ?? ''}
                  onChange={(event) => set('unit', event.target.value || null)}
                  placeholder="bar, Nm, g, pumps"
                />
              </Field>
              <Field label="Target value" hint="Optional. What the standard aims for.">
                <Input
                  inputMode="decimal"
                  value={draft.targetValue ?? ''}
                  onChange={(event) => set('targetValue', numberOrNull(event.target.value))}
                />
              </Field>
            </div>

            <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-lg border-2 border-steel-300 bg-white p-3">
              <input
                type="checkbox"
                className="mt-0.5 size-5"
                checked={draft.recordOnly}
                onChange={(event) => {
                  const on = event.target.checked;
                  set('recordOnly', on);
                  // Clear limits so the contradictory state cannot be saved.
                  if (on) {
                    set('minValue', null);
                    set('maxValue', null);
                    set('tolerance', null);
                  }
                }}
              />
              <span className="text-sm">
                <span className="block font-bold text-steel-900">
                  Record the reading only — do not score it
                </span>
                <span className="mt-0.5 block text-steel-600">
                  Use this when the source standard gives a target but no acceptance tolerance
                  (for example “Torque 25 Nm”). The reading is captured and shown, but it never
                  produces a pass, a fail or a Problem Tag. Inventing a tolerance here would be
                  inventing an engineering standard.
                </span>
              </span>
            </label>

            {!draft.recordOnly ? (
              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                <Field label="Minimum">
                  <Input
                    inputMode="decimal"
                    value={draft.minValue ?? ''}
                    onChange={(event) => set('minValue', numberOrNull(event.target.value))}
                  />
                </Field>
                <Field label="Maximum">
                  <Input
                    inputMode="decimal"
                    value={draft.maxValue ?? ''}
                    onChange={(event) => set('maxValue', numberOrNull(event.target.value))}
                  />
                </Field>
                <Field label="± Tolerance" hint="Around the target.">
                  <Input
                    inputMode="decimal"
                    value={draft.tolerance ?? ''}
                    onChange={(event) => set('tolerance', numberOrNull(event.target.value))}
                  />
                </Field>
              </div>
            ) : null}

            <Field
              className="mt-3"
              label="Engineering standard"
              hint="Link a controlled standard instead of typing limits. When engineering approves it, this task starts being scored — even on an already-published checklist."
            >
              <Select
                value={draft.engineeringStandardId ?? ''}
                onChange={(event) => set('engineeringStandardId', event.target.value || null)}
              >
                <option value="">Not linked</option>
                {(standards?.data ?? []).map((standard) => (
                  <option key={standard.id} value={standard.id}>
                    {standard.code} — {standard.parameter} ({humanise(standard.status)})
                  </option>
                ))}
              </Select>
            </Field>

            {linkedStandard ? (
              <p className="mt-2 text-xs text-steel-600">
                {linkedStandard.status === 'ACTIVE' &&
                (linkedStandard.minValue != null || linkedStandard.maxValue != null) ? (
                  <>
                    <Badge tone="ok">Scoring</Badge> Readings are judged against{' '}
                    {linkedStandard.minValue} to {linkedStandard.maxValue}{' '}
                    {linkedStandard.unit ?? ''}.
                  </>
                ) : (
                  <>
                    <Badge tone="warn">Not in service</Badge> This standard has no approved
                    tolerance yet, so the reading will be recorded without being scored.
                  </>
                )}
              </p>
            ) : null}
          </fieldset>
        ) : null}

        {/* --- Options --- */}
        {isChoice ? (
          <fieldset className="rounded-lg border border-steel-200 p-3">
            <legend className="px-1 text-sm font-bold text-steel-800">Options</legend>
            <p className="mb-2 text-xs text-steel-500">
              Mark which choices mean something is wrong — those are what raise an abnormality.
            </p>
            <div className="space-y-2">
              {draft.options.map((option, index) => (
                <div key={index} className="flex items-end gap-2">
                  <Field label={index === 0 ? 'Label' : ''} className="flex-1">
                    <Input
                      value={option.label}
                      onChange={(event) => {
                        const next = [...draft.options];
                        next[index] = {
                          ...option,
                          label: event.target.value,
                          value:
                            option.value ||
                            event.target.value.toUpperCase().replace(/[^A-Z0-9]+/g, '_'),
                        };
                        set('options', next);
                      }}
                      placeholder="Good"
                    />
                  </Field>
                  <Field label={index === 0 ? 'Outcome' : ''} className="w-40">
                    <Select
                      value={option.result}
                      onChange={(event) => {
                        const next = [...draft.options];
                        next[index] = { ...option, result: event.target.value };
                        set('options', next);
                      }}
                    >
                      <option value="NORMAL">Normal</option>
                      <option value="ABNORMAL">Abnormal</option>
                    </Select>
                  </Field>
                  <Button
                    variant="ghost"
                    icon={Trash2}
                    aria-label={`Remove option ${option.label}`}
                    className="mb-0.5"
                    onClick={() => set('options', draft.options.filter((_, i) => i !== index))}
                  />
                </div>
              ))}
            </div>
            <Button
              variant="secondary"
              size="sm"
              icon={Plus}
              className="mt-2"
              onClick={() =>
                set('options', [...draft.options, { value: '', label: '', result: 'NORMAL' }])
              }
            >
              Add option
            </Button>
          </fieldset>
        ) : null}

        {/* --- Behaviour --- */}
        <fieldset className="rounded-lg border border-steel-200 p-3">
          <legend className="px-1 text-sm font-bold text-steel-800">
            What happens when it is abnormal
          </legend>
          <Field label="Required follow-up" hint={FAILURE_HELP[draft.failureBehaviour]}>
            <Select
              value={draft.failureBehaviour}
              onChange={(event) => set('failureBehaviour', event.target.value)}
            >
              {FAILURE_BEHAVIOURS.map((behaviour) => (
                <option key={behaviour} value={behaviour}>
                  {humanise(behaviour)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Default severity" className="mt-3">
            <Select
              value={draft.defaultSeverity}
              onChange={(event) => set('defaultSeverity', event.target.value)}
            >
              {SEVERITIES.map((severity) => (
                <option key={severity} value={severity}>
                  {humanise(severity)}
                </option>
              ))}
            </Select>
          </Field>

          <div className="mt-3 space-y-2">
            <Toggle
              label="Answering this task is required"
              checked={draft.isRequired}
              onChange={(value) => set('isRequired', value)}
            />
            <Toggle
              label="Always require a photo"
              checked={draft.requiresPhoto}
              onChange={(value) => set('requiresPhoto', value)}
            />
            <Toggle
              label="Require a photo when it is abnormal"
              checked={draft.requiresPhotoOnFail}
              onChange={(value) => set('requiresPhotoOnFail', value)}
            />
          </div>
        </fieldset>

        {/* --- Warnings --- */}
        <fieldset className="rounded-lg border border-steel-200 p-3">
          <legend className="px-1 text-sm font-bold text-steel-800">Warnings</legend>

          <Toggle
            label="Safety critical — cannot be marked “not applicable”"
            checked={draft.isSafetyCritical}
            onChange={(value) => set('isSafetyCritical', value)}
          />

          <Field label="Safety warning" className="mt-3" hint="Shown in red, above the answer buttons.">
            <Textarea
              value={draft.safetyWarning ?? ''}
              onChange={(event) => set('safetyWarning', event.target.value || null)}
              rows={2}
              placeholder="e.g. Hot surface! Wear heat-resistant gloves."
            />
          </Field>

          <Field label="Quality warning" className="mt-3">
            <Textarea
              value={draft.qualityWarning ?? ''}
              onChange={(event) => set('qualityWarning', event.target.value || null)}
              rows={2}
              placeholder="e.g. Dirty jaws = poor seal = product leak."
            />
          </Field>
        </fieldset>

        <Field label="Linked One-Point Lesson" hint="Operators can open it while doing this task.">
          <Select
            value={draft.oplId ?? ''}
            onChange={(event) => set('oplId', event.target.value || null)}
          >
            <option value="">Not linked</option>
            {(opls?.data ?? []).map((opl) => (
              <option key={opl.id} value={opl.id}>
                {opl.oplNo} — {opl.title}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Modal>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border border-steel-300 px-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="size-5"
      />
      <span className="text-sm font-medium text-steel-800">{label}</span>
    </label>
  );
}

function numberOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed.replace(',', '.'));
  return Number.isNaN(parsed) ? null : parsed;
}
